import { useEffect, type ReactNode } from 'react'

import { applyDevDocumentTitle } from '@/shared/lib/runtime/dev-environment'

type MetaTag = { name: string; content: string }
type LinkTag = { rel: string; href: string; sizes?: string }

let titleTemplate: string | null = null
let defaultTitle: string | null = null

function formatTitle(title: string) {
  if (titleTemplate?.includes('%s')) {
    return titleTemplate.replace('%s', title)
  }
  return title
}

function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.name = name
    document.head.appendChild(el)
  }
  el.content = content
}

function upsertLink(rel: string, href: string, sizes?: string) {
  const selector = sizes
    ? `link[rel="${rel}"][sizes="${sizes}"]`
    : `link[rel="${rel}"]:not([sizes])`
  let el = document.querySelector<HTMLLinkElement>(selector)
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    if (sizes) el.setAttribute('sizes', sizes)
    document.head.appendChild(el)
  }
  el.href = href
}

function removeRobotsNoindex() {
  document
    .querySelectorAll('meta[name="robots"]')
    .forEach(el => el.remove())
}

export type SiteMetaProps = {
  lang?: string
  defaultTitle?: string
  titleTemplate?: string
  description?: string
  faviconHref?: string
  linkTags?: LinkTag[]
  metaTags?: MetaTag[]
}

/** App-wide defaults (favicon, title template, viewport). Mount once per tenant shell. */
export function SiteMeta({
  lang = 'pt_BR',
  defaultTitle: nextDefaultTitle,
  titleTemplate: nextTitleTemplate,
  description,
  faviconHref,
  linkTags = [],
  metaTags = []
}: SiteMetaProps) {
  useEffect(() => {
    document.documentElement.lang = lang
    titleTemplate = nextTitleTemplate ?? null
    defaultTitle = nextDefaultTitle ?? null

    if (nextDefaultTitle) {
      document.title = applyDevDocumentTitle(nextDefaultTitle)
    }

    if (description) {
      upsertMeta('description', description)
    }

    for (const tag of metaTags) {
      upsertMeta(tag.name, tag.content)
    }

    const links = faviconHref
      ? faviconLinkTags(faviconHref)
      : linkTags

    for (const link of links) {
      if (link.href) upsertLink(link.rel, link.href, link.sizes)
    }
  }, [
    lang,
    nextDefaultTitle,
    nextTitleTemplate,
    description,
    faviconHref,
    linkTags,
    metaTags
  ])

  return null
}

export type PageMetaProps = {
  title?: string
  description?: string
  noindex?: boolean
  linkTags?: LinkTag[]
  metaTags?: MetaTag[]
  children?: ReactNode
}

/** Per-screen title and optional meta (toolbar, quiz play, public pages). */
export function PageMeta({
  title,
  description,
  noindex,
  linkTags = [],
  metaTags = [],
  children
}: PageMetaProps) {
  useEffect(() => {
    if (title) {
      document.title = applyDevDocumentTitle(formatTitle(title))
    } else if (defaultTitle) {
      document.title = applyDevDocumentTitle(defaultTitle)
    }

    if (description) {
      upsertMeta('description', description)
    }

    for (const tag of metaTags) {
      upsertMeta(tag.name, tag.content)
    }

    for (const link of linkTags) {
      if (link.href) upsertLink(link.rel, link.href, link.sizes)
    }

    if (noindex) {
      upsertMeta('robots', 'noindex,nofollow')
    }

    return () => {
      if (noindex) removeRobotsNoindex()
    }
  }, [title, description, noindex, linkTags, metaTags])

  return <>{children}</>
}

export function faviconLinkTags(
  faviconUrl: string | undefined
): LinkTag[] {
  const href = faviconUrl || '/placeholders/icon.png'
  return [
    { rel: 'icon', href, sizes: '16x16' },
    { rel: 'icon', href, sizes: '32x32' },
    { rel: 'apple-touch-icon', href, sizes: '76x76' },
    { rel: 'shortcut icon', href }
  ]
}
